import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Crown, CheckCircle, X, ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import RazorpayButton from '../components/payment/RazorpayButton';
import { useAuth } from '../hooks/useAuth';
import { trackEvent } from '../utils/posthog';

const PricingPage: React.FC = () => {
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user came from within the app (e.g., chat feature)
  const isInAppNavigation = location.state?.from === 'app';

  // Calculate if early bird discount applies (before June 31, 2025)
  const isEarlyBird = () => {
    const earlyBirdEndDate = new Date('2025-06-31T23:59:59');
    const currentDate = new Date();
    return currentDate <= earlyBirdEndDate;
  };

  // Calculate price based on early bird status
  const getPrice = () => {
    const regularPrice = 499;
    return isEarlyBird() ? Math.round(regularPrice * 0.5) : regularPrice;
  };

  const price = getPrice();

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'pricing_page',
      is_early_bird: isEarlyBird(),
      price: price
    });
  }, [price]);

  const handlePaymentSuccess = (paymentId: string) => {
    setPaymentStatus('success');
    
    // Track successful payment
    trackEvent('payment_success', {
      payment_id: paymentId,
      amount: price,
      is_early_bird: isEarlyBird()
    });
    
    console.log('Payment successful:', paymentId);
  };

  const handlePaymentError = (error: any) => {
    setPaymentStatus('error');
    setPaymentError(error.message || 'Payment failed. Please try again.');
    
    // Track payment error
    trackEvent('payment_error', {
      error_message: error.message,
      amount: price,
      is_early_bird: isEarlyBird()
    });
  };

  // Render different layouts based on navigation source
  const renderContent = () => (
    <div className="container mx-auto px-4">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-lg text-gray-600">
          Choose the plan that works for you
        </p>
      </div>

      {paymentStatus === 'success' && (
        <div className="max-w-md mx-auto mb-8 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          Payment successful! Premium features activated.
        </div>
      )}

      {paymentStatus === 'error' && (
        <div className="max-w-md mx-auto mb-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {paymentError}
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Free Plan */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-2">Free</h2>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold">₹0</span>
              </div>
              <p className="text-gray-500 mt-1 mb-6">
                Basic features for college exploration
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>College basic information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>General cutoff ranges</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Stream-wise college rankings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Compare up to 3 colleges</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-gray-300 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Personalized predictions</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-gray-300 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Top 10 college matches</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-gray-300 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Historical cutoff trends</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-gray-300 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Round-wise admission insights</span>
                </li>
              </ul>
              
              {!user && (
                <Link 
                  to="/register" 
                  className="block w-full bg-white border border-gray-300 rounded-lg py-3 text-center font-medium text-gray-800 hover:bg-gray-50 transition-colors"
                  onClick={() => trackEvent('cta_click', { cta_type: 'get_started_free', location: 'pricing_page' })}
                >
                  Get Started
                </Link>
              )}
            </div>
          </div>
          
          {/* Premium Plan */}
          <div className="bg-[#F0E9FF] rounded-xl shadow-md overflow-hidden relative">
            <div className="absolute top-0 right-0 bg-[#7B4AE2] text-white py-1 px-4 text-xs font-bold uppercase tracking-wider">
              Recommended
            </div>
            {isEarlyBird() && (
              <div className="absolute top-0 left-0 bg-[#FFD166] text-[#7B4AE2] py-1 px-4 text-xs font-bold uppercase tracking-wider">
                50% Off - Early Bird
              </div>
            )}
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-2">Premium</h2>
              <div className="flex items-baseline">
                {isEarlyBird() && (
                  <span className="text-2xl font-medium line-through text-gray-400 mr-2">₹499</span>
                )}
                <span className="text-4xl font-bold">₹{price}</span>
                <span className="ml-2 text-sm text-gray-600">one-time payment</span>
              </div>
              {isEarlyBird() && (
                <p className="text-[#7B4AE2] font-medium text-sm mt-1">
                  Early bird offer ends June 31, 2025
                </p>
              )}
              <p className="text-gray-600 mt-1 mb-6">
                Full access to all features and predictions
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>College basic information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>General cutoff ranges</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Stream-wise college rankings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Compare up to 10 colleges</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Personalized predictions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Top 10 college matches</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Historical cutoff trends</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Round-wise admission insights</span>
                </li>
              </ul>
              
              {user ? (
                user.isPremium ? (
                  <div className="text-green-600 font-medium text-center py-3">
                    You already have premium access!
                  </div>
                ) : (
                  <RazorpayButton
                    amount={price}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                )
              ) : (
                <Link 
                  to="/register" 
                  className="block w-full bg-[#7B4AE2] text-white text-center py-3 px-6 rounded-lg font-medium hover:bg-[#6A3ACF] transition-colors"
                  onClick={() => trackEvent('cta_click', { cta_type: 'sign_up_premium', location: 'pricing_page', price: price })}
                >
                  Sign Up Now
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-20 max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">How accurate are the predictions?</h3>
            <p className="text-gray-600">
              Our predictions and guidance are generated using historical data and advanced algorithmic models, which currently demonstrate an accuracy rate of over 90%. While we continuously update and refine these models for better performance, all predictions are indicative and do not guarantee actual admission outcomes.
Cutoffs and trends may vary each year due to multiple factors beyond our control.
11thadmissions.com and its affiliates shall not be held liable for any decisions made based on the predictive outputs provided by the platform. Users are encouraged to use their own discretion and consult official sources when making admission-related choices.
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">Can I use the service for multiple students?</h3>
            <p className="text-gray-600">
              Each premium subscription is valid for one student profile. If you need to use the service for multiple students, you'll need separate subscriptions for each student.
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">Is my data secure?</h3>
            <p className="text-gray-600">
              Yes, we take data security very seriously. All personal information and academic records are encrypted and stored securely. We never share your data with third parties without your consent.
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">How do I get a refund?</h3>
            <p className="text-gray-600">
              If you're not satisfied with our premium services, you can request a refund within 7 days of purchase. Please contact our support team with your order details for assistance via whatsapp.
            </p>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="mt-20 bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-12 md:py-16 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Find Your Perfect College?
          </h2>
          <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of students who have found their ideal college match using our platform
          </p>
          <Link 
            to={user ? '/dashboard' : '/register'}
            className="inline-flex items-center px-8 py-4 bg-white text-[#7B4AE2] rounded-lg hover:bg-gray-50"
            onClick={() => trackEvent('cta_click', { 
              cta_type: user ? 'go_to_dashboard' : 'get_started', 
              location: 'pricing_page_footer' 
            })}
          >
            {user ? 'Go to Dashboard' : 'Get Started Free'}
          </Link>
        </div>
      </div>
    </div>
  );

  // If user came from within the app, use the app layout
  if (isInAppNavigation || user) {
    return (
      <div className="min-h-screen bg-[#F9F7FE] py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <Link 
              to="/dashboard"
              className="text-gray-600 hover:text-gray-900 flex items-center"
              onClick={() => trackEvent('navigation_click', { link_name: 'back_to_dashboard', location: 'pricing_page' })}
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          {renderContent()}
        </div>
      </div>
    );
  }

  // Otherwise, use the landing page layout
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow py-16 bg-[#F9F7FE]">
        {renderContent()}
      </div>
      <Footer />
    </div>
  );
};

export default PricingPage;